/**
 * RestoID — MFA factor picker rows.
 * ---------------------------------------------------------------------------
 * RestoID OptionRows over the PRISTINE upstream MfaFactorList logic (same hooks
 * + routing in `handleSelectFactor`). Replaces Logto's MfaFactorButton list
 * (e.g. "Пароль" with a USB icon for passkey, verbose TOTP) with the design's
 * clean rows. TOTP suggests Яндекс Ключ first for the CIS market.
 */
import { MfaFactor, SignInIdentifier } from '@logto/schemas';
import { type ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import useSendMfaVerificationCode from '@/hooks/use-send-mfa-verification-code';
import useStartTotpBinding from '@/hooks/use-start-totp-binding';
import useStartWebAuthnProcessing from '@/hooks/use-start-webauthn-processing';
import { UserMfaFlow } from '@/types';
import { type MfaFlowState } from '@/types/guard';

import { useCopy } from '../copy';

import { GhostAction, OptionRow, Stack } from './restoid';

/**
 * "Переключиться на другой метод" — RestoID GhostAction (swap icon), centred.
 * Replaces the upstream SwitchMfaFactorsLink (a left lime TextLink). Navigates
 * back to the factor picker with the flow state, like the upstream link.
 */
export const MfaSwitchLink = ({
  flow,
  flowState,
}: {
  flow: UserMfaFlow;
  flowState: MfaFlowState;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigateWithPreservedSearchParams();

  if (flowState.availableFactors.length < 2) {
    return null;
  }

  return (
    <GhostAction icon="swap" onClick={() => navigate(`/${flow}`, { state: flowState })}>
      {t('mfa.link_another_mfa_factor')}
    </GhostAction>
  );
};

const MfaFactorRows = ({ flow, flowState }: { flow: UserMfaFlow; flowState: MfaFlowState }) => {
  const c = useCopy();
  const startTotpBinding = useStartTotpBinding();
  const startWebAuthnProcessing = useStartWebAuthnProcessing();
  const navigate = useNavigateWithPreservedSearchParams();
  const { availableFactors, isWebAuthnUsedAsSignInPasskey } = flowState;
  const { onSubmit: sendMfaVerificationCode } = useSendMfaVerificationCode();

  // Mirrors upstream MfaFactorList.handleSelectFactor 1:1.
  const handleSelectFactor = useCallback(
    async (factor: MfaFactor) => {
      if (factor === MfaFactor.TOTP && flow === UserMfaFlow.MfaBinding) {
        return startTotpBinding(flowState);
      }
      if (factor === MfaFactor.WebAuthn) {
        return startWebAuthnProcessing(flow, flowState);
      }
      if (factor === MfaFactor.EmailVerificationCode && flow === UserMfaFlow.MfaVerification) {
        await sendMfaVerificationCode(SignInIdentifier.Email, flowState);
        return;
      }
      if (factor === MfaFactor.PhoneVerificationCode && flow === UserMfaFlow.MfaVerification) {
        await sendMfaVerificationCode(SignInIdentifier.Phone, flowState);
        return;
      }
      navigate(`/${flow}/${factor}`, { state: flowState });
    },
    [flow, flowState, navigate, sendMfaVerificationCode, startTotpBinding, startWebAuthnProcessing]
  );

  const rowFor = (factor: MfaFactor): { icon: string; title: ReactNode; desc: ReactNode } => {
    switch (factor) {
      case MfaFactor.WebAuthn: {
        return { icon: 'finger', title: c('mfaKeyT'), desc: c('mfaKeyD') };
      }
      case MfaFactor.TOTP: {
        return { icon: 'smartphone', title: c('mfaTotpT'), desc: c('mfaTotpD') };
      }
      case MfaFactor.BackupCode: {
        return { icon: 'key', title: c('mfaBackupT'), desc: c('mfaBackupD') };
      }
      case MfaFactor.EmailVerificationCode: {
        return { icon: 'mail', title: c('mfaEmailT'), desc: c('mfaEmailD') };
      }
      case MfaFactor.PhoneVerificationCode: {
        return { icon: 'smartphone', title: c('mfaPhoneT'), desc: c('mfaPhoneD') };
      }
    }
  };

  return (
    <Stack gap={10}>
      {availableFactors.map((factor) => {
        const isEmailOrPhone =
          factor === MfaFactor.EmailVerificationCode || factor === MfaFactor.PhoneVerificationCode;
        const maskedIdentifier = isEmailOrPhone ? flowState.maskedIdentifiers?.[factor] : undefined;
        const isWebAuthnBound =
          factor === MfaFactor.WebAuthn && Boolean(isWebAuthnUsedAsSignInPasskey);
        // In suggestion mode, an already-bound factor is shown disabled upstream; we
        // simply omit it (the picker only offers what can still be added).
        const isDisabled =
          Boolean(flowState.suggestion) && (Boolean(maskedIdentifier) || isWebAuthnBound);

        if (isDisabled) {
          return null;
        }

        const { icon, title, desc } = rowFor(factor);

        return (
          <OptionRow
            key={factor}
            icon={icon}
            title={title}
            desc={maskedIdentifier ?? desc}
            onClick={async () => {
              await handleSelectFactor(factor);
            }}
          />
        );
      })}
    </Stack>
  );
};

export default MfaFactorRows;
